// Forked once from studioCloud/src/data/resourcesMetadata.js (2026-10-16) — see brightivity-web/FORK.md.
// Resources Metadata - Maps user searches to manual sections
// Structure optimized for Fuse.js fuzzy search with natural language queries
// endPage = next section's start page (content may continue to top of that page)

export const manuals = {
  't600': {
    id: 't600',
    name: 'T650 Manual',
    file: 'T650_manual.pdf',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    description: 'User guide for HP DesignJet T200 and T600 series',
    pageOffset: 7  // PDF has 7 intro/TOC pages before page 1
  },
  't850': {
    id: 't850',
    name: 'T850/T950 Manual',
    file: 'T850_manual.pdf',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    description: 'User guide for HP DesignJet T850 and T950 series',
    pageOffset: 9  // PDF has 9 intro/TOC pages before page 1
  },
  'z6': {
    id: 'z6',
    name: 'Z6/Z9+ Manual',
    file: 'z6_user_manual.pdf',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    description: 'User guide for HP DesignJet Z6 and Z9+ series',
    pageOffset: 8  // PDF has 8 intro/TOC pages before page 1
  },
  't870': {
    id: 't870',
    name: 'T870 Manual',
    file: 'T870_manual.pdf',
    printers: ['T870', 'T870 MFP'],
    description: 'User guide for HP DesignJet T870 series',
    pageOffset: 7  // PDF has 7 intro/TOC pages before page 1
  }
};

export const categories = [
  { id: 'all', label: 'All' },
  { id: 'setup', label: 'Setup' },
  { id: 'paper', label: 'Paper' },
  { id: 'printing', label: 'Printing' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'troubleshooting', label: 'Troubleshooting' }
];

export const printerFamilies = [
  { id: 'all', label: 'All Printers' },
  { id: 't200', label: 'T200 Series', printers: ['T210', 'T230', 'T250'] },
  { id: 't600', label: 'T600 Series', printers: ['T630', 'T650'] },
  { id: 't850', label: 'T850/T950 Series', printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'] },
  { id: 't870', label: 'T870 Series', printers: ['T870', 'T870 MFP'] },
  { id: 'z6', label: 'Z6/Z9+ Series', printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'] }
];

// Main resources metadata
// Each entry includes:
// - title: Official section name
// - keywords: Technical terms
// - naturalQueries: How non-technical users might describe the problem
// - page: Start page number in the PDF
// - endPage: End page = next section's start page (content may span to top of that page)
// - manual: Which manual file to open

export const resources = [
  // ============ SETUP ============
  {
    id: 't600-turn-on-off',
    title: 'Turn the Printer On and Off',
    category: 'setup',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 8,
    endPage: 10,  // next: Control panel at 10
    keywords: ['power', 'on', 'off', 'start', 'shutdown', 'turn on', 'turn off'],
    naturalQueries: [
      'how to start printer',
      'printer won\'t turn on',
      'where is power button',
      'shut down printer',
      'power off'
    ],
    description: 'How to properly turn the printer on and off'
  },
  {
    id: 't600-control-panel',
    title: 'Control Panel',
    category: 'setup',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 10,
    endPage: 11,  // next: Printer solutions at 11
    keywords: ['control panel', 'screen', 'display', 'buttons', 'menu', 'interface'],
    naturalQueries: [
      'how to use screen',
      'what do buttons do',
      'navigate menu',
      'front panel',
      'touchscreen'
    ],
    description: 'Understanding the printer control panel and menus'
  },
  {
    id: 't600-connect-network',
    title: 'Connect the Printer to Your Network',
    category: 'setup',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 16,
    endPage: 17,  // next: Set up your device at 17
    keywords: ['network', 'wifi', 'wireless', 'ethernet', 'connect', 'internet', 'lan'],
    naturalQueries: [
      'connect to wifi',
      'setup wireless',
      'printer not connecting',
      'network setup',
      'can\'t find printer on network',
      'add printer to wifi'
    ],
    description: 'How to connect the printer to your wired or wireless network'
  },
  {
    id: 't600-configure-network',
    title: 'Configure Network Settings',
    category: 'setup',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 27,
    endPage: 29,  // next: Configure firewall at 29
    keywords: ['network settings', 'ip address', 'dns', 'dhcp', 'static ip'],
    naturalQueries: [
      'change ip address',
      'network configuration',
      'ip settings',
      'printer ip'
    ],
    description: 'Advanced network configuration options'
  },

  // ============ PAPER HANDLING ============
  {
    id: 't600-load-roll-spindle',
    title: 'Load a Roll onto the Spindle',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 32,
    endPage: 33,  // next: Load roll into printer at 33
    keywords: ['roll', 'spindle', 'load paper', 'paper roll', 'install roll'],
    naturalQueries: [
      'put paper roll in',
      'change paper roll',
      'new roll of paper',
      'install paper',
      'load roll'
    ],
    description: 'How to load a paper roll onto the spindle'
  },
  {
    id: 't600-load-roll-printer',
    title: 'Load a Roll into the Printer',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 33,
    endPage: 34,  // next: Unload roll at 34
    keywords: ['load roll', 'insert roll', 'feed paper', 'roll paper'],
    naturalQueries: [
      'put roll in printer',
      'feed paper roll',
      'paper not feeding',
      'insert roll paper'
    ],
    description: 'How to load the paper roll into the printer'
  },
  {
    id: 't600-unload-roll',
    title: 'Unload a Roll',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 34,
    endPage: 35,  // next: Load single sheet at 35
    keywords: ['unload', 'remove roll', 'take out paper', 'eject roll'],
    naturalQueries: [
      'remove paper roll',
      'take out roll',
      'change paper',
      'eject paper'
    ],
    description: 'How to remove a paper roll from the printer'
  },
  {
    id: 't600-load-single-sheet',
    title: 'Load a Single Sheet',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 35,
    endPage: 36,  // next: Unload single sheet at 36
    keywords: ['single sheet', 'sheet paper', 'manual feed', 'one page'],
    naturalQueries: [
      'print on one sheet',
      'load single page',
      'use sheet paper',
      'manual paper feed'
    ],
    description: 'How to load a single sheet of paper'
  },
  {
    id: 't600-multi-sheet-tray',
    title: 'Use the Multi-Sheet Tray',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 36,
    endPage: 37,  // next: View information at 37
    keywords: ['tray', 'multi-sheet', 'paper tray', 'sheet feeder'],
    naturalQueries: [
      'use paper tray',
      'load tray',
      'multiple sheets',
      'sheet tray'
    ],
    description: 'How to use the multi-sheet paper tray'
  },
  {
    id: 't600-change-drying-time',
    title: 'Change the Drying Time',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 38,
    endPage: 38,  // next: Turn cutter on/off at 38 (same page)
    keywords: ['drying', 'dry time', 'ink dry', 'smudge'],
    naturalQueries: [
      'ink smudging',
      'print wet',
      'ink not dry',
      'smearing'
    ],
    description: 'How to adjust drying time for different paper types'
  },
  {
    id: 't600-cutter-on-off',
    title: 'Turn the Automatic Cutter On and Off',
    category: 'paper',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 38,
    endPage: 40,  // next: Chapter 6 Printing at 40
    keywords: ['cutter', 'auto cut', 'paper cutter', 'cut paper'],
    naturalQueries: [
      'paper not cutting',
      'disable cutter',
      'auto cut off',
      'stop cutting'
    ],
    description: 'How to enable or disable the automatic paper cutter'
  },

  // ============ PRINTING ============
  {
    id: 't600-wifi-direct',
    title: 'Print using Wi-Fi Direct',
    category: 'printing',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 46,
    endPage: 48,  // next: HP Click at 48
    keywords: ['wifi direct', 'wireless print', 'direct connect', 'no network'],
    naturalQueries: [
      'print without network',
      'direct wireless',
      'connect phone to printer',
      'print from phone'
    ],
    description: 'How to print using Wi-Fi Direct without a network'
  },
  {
    id: 't600-hp-click',
    title: 'Easy Printing with HP Click',
    category: 'printing',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 48,
    endPage: 49,  // next: Print driver at 49
    keywords: ['hp click', 'easy print', 'simple print', 'hp software'],
    naturalQueries: [
      'easiest way to print',
      'hp click software',
      'simple printing'
    ],
    description: 'Using HP Click for easy one-click printing'
  },
  {
    id: 't600-print-driver',
    title: 'Printing from an Application Using a Printer Driver',
    category: 'printing',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 49,
    endPage: 56,  // next: Chapter 7 at 56
    keywords: ['driver', 'print driver', 'application', 'software'],
    naturalQueries: [
      'print from computer',
      'install driver',
      'print settings',
      'driver settings'
    ],
    description: 'How to print from applications using the printer driver'
  },
  {
    id: 't600-print-correct-colors',
    title: 'Print a Document with the Correct Colors',
    category: 'printing',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 59,
    endPage: 62,  // next: AutoCAD at 62
    keywords: ['color', 'colors', 'color management', 'accurate colors', 'color profile'],
    naturalQueries: [
      'colors look wrong',
      'colors don\'t match',
      'wrong colors',
      'color settings',
      'fix colors'
    ],
    description: 'How to ensure your prints have accurate colors'
  },

  // ============ MAINTENANCE ============
  {
    id: 't600-check-ink-status',
    title: 'Check Ink Cartridge Status',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 72,
    endPage: 72,  // next: Replace ink at 72 (same page)
    keywords: ['ink level', 'ink status', 'cartridge level', 'how much ink'],
    naturalQueries: [
      'how much ink left',
      'check ink',
      'ink levels',
      'running low on ink'
    ],
    description: 'How to check the ink levels in your cartridges'
  },
  {
    id: 't600-replace-ink-cartridge',
    title: 'Replace an Ink Cartridge',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 72,
    endPage: 74,  // next: About printhead at 74
    keywords: ['replace ink', 'change ink', 'new cartridge', 'ink cartridge', 'install ink'],
    naturalQueries: [
      'ink empty',
      'change ink',
      'put in new ink',
      'cartridge replacement',
      'out of ink'
    ],
    description: 'Step-by-step guide to replace ink cartridges'
  },
  {
    id: 't600-about-printhead',
    title: 'About the Printhead',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 74,
    endPage: 75,  // next: Chapter 9 Maintaining at 75
    keywords: ['printhead', 'print head', 'head info'],
    naturalQueries: [
      'what is printhead',
      'printhead information',
      'head details'
    ],
    description: 'Information about the printhead component'
  },
  {
    id: 't600-clean-printhead',
    title: 'Clean the Printhead',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 78,
    endPage: 78,  // next: Align at 78 (same page)
    keywords: ['clean printhead', 'printhead cleaning', 'head clean', 'nozzle clean'],
    naturalQueries: [
      'streaks on print',
      'lines on paper',
      'poor print quality',
      'missing colors',
      'banding',
      'printhead clogged'
    ],
    description: 'How to clean the printhead to fix print quality issues'
  },
  {
    id: 't600-align-printhead',
    title: 'Align the Printhead',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 78,
    endPage: 79,  // next: Replace printhead at 79
    keywords: ['align', 'alignment', 'printhead alignment', 'calibrate'],
    naturalQueries: [
      'print not straight',
      'text blurry',
      'lines not aligned',
      'calibrate printer'
    ],
    description: 'How to align the printhead for better print quality'
  },
  {
    id: 't600-replace-printhead',
    title: 'Replace the Printhead',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 79,
    endPage: 82,  // next: Clean encoder strip at 82
    keywords: ['replace printhead', 'new printhead', 'change printhead', 'printhead replacement'],
    naturalQueries: [
      'printhead broken',
      'printhead error',
      'need new printhead',
      'printhead not working',
      'install printhead'
    ],
    description: 'Step-by-step guide to replace the printhead'
  },
  {
    id: 't600-clean-encoder-strip',
    title: 'Clean the Encoder Strip',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 82,
    endPage: 83,  // next: Recalibrate at 83
    keywords: ['encoder strip', 'encoder', 'strip cleaning'],
    naturalQueries: [
      'weird lines',
      'print position wrong',
      'carriage errors'
    ],
    description: 'How to clean the encoder strip'
  },
  {
    id: 't600-recalibrate-paper',
    title: 'Recalibrate the Paper Advance',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 83,
    endPage: 83,  // next: Move/store at 83 (same page)
    keywords: ['calibrate', 'paper advance', 'recalibrate', 'paper feed'],
    naturalQueries: [
      'paper feeding wrong',
      'spacing issues',
      'paper advance problem'
    ],
    description: 'How to recalibrate the paper advance mechanism'
  },
  {
    id: 't600-move-store-printer',
    title: 'Move or Store the Printer',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 83,
    endPage: 84,  // next: Firmware update at 84
    keywords: ['move', 'transport', 'store', 'relocate', 'storage'],
    naturalQueries: [
      'moving printer',
      'transport printer',
      'store printer',
      'relocate printer'
    ],
    description: 'How to safely move or store the printer'
  },
  {
    id: 't600-firmware-update',
    title: 'Firmware Update',
    category: 'maintenance',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 84,
    endPage: 85,  // next: Software update at 84, then Maintenance kits at 85
    keywords: ['firmware', 'update', 'upgrade', 'software update'],
    naturalQueries: [
      'update printer',
      'new firmware',
      'printer update',
      'upgrade firmware'
    ],
    description: 'How to update the printer firmware'
  },

  // ============ TROUBLESHOOTING - PAPER ============
  {
    id: 't600-paper-load-problem',
    title: 'Paper Cannot be Loaded Successfully',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 91,
    endPage: 94,  // next: Paper type not in driver at 94
    keywords: ['paper load', 'won\'t load', 'paper problem', 'loading error'],
    naturalQueries: [
      'paper won\'t load',
      'can\'t load paper',
      'paper not going in',
      'paper loading problem',
      'paper error'
    ],
    description: 'Troubleshooting paper loading issues'
  },
  {
    id: 't600-roll-jam',
    title: 'Roll has Jammed',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 95,
    endPage: 97,  // next: Prevent jam at 97
    keywords: ['jam', 'paper jam', 'stuck', 'jammed', 'paper stuck'],
    naturalQueries: [
      'paper stuck',
      'paper jam',
      'clear jam',
      'paper won\'t come out',
      'jammed paper'
    ],
    description: 'How to clear a paper jam from the roll'
  },
  {
    id: 't600-prevent-jam',
    title: 'Prevent a Jam',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 97,
    endPage: 98,  // next: Tray jam at 98
    keywords: ['prevent jam', 'avoid jam', 'jam prevention'],
    naturalQueries: [
      'stop paper jams',
      'avoid jams',
      'keep jamming',
      'frequent jams'
    ],
    description: 'Tips to prevent paper jams'
  },
  {
    id: 't600-tray-jam',
    title: 'Multi-Sheet Tray has Jammed',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 98,
    endPage: 99,  // next: Strip stays at 99
    keywords: ['tray jam', 'sheet jam', 'tray stuck'],
    naturalQueries: [
      'tray jammed',
      'sheet stuck',
      'tray paper jam'
    ],
    description: 'How to clear a jam in the multi-sheet tray'
  },
  {
    id: 't600-cutter-problem',
    title: 'Cutter Does Not Cut Well',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 100,
    endPage: 101,  // next: Roll loose at 101
    keywords: ['cutter', 'cut', 'cutting problem', 'paper cut'],
    naturalQueries: [
      'cutter not working',
      'paper not cutting',
      'bad cut',
      'uneven cut'
    ],
    description: 'Troubleshooting paper cutter issues'
  },

  // ============ TROUBLESHOOTING - PRINT QUALITY ============
  {
    id: 't600-banding',
    title: 'Horizontal Lines Across the Image (Banding)',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 102,
    endPage: 103,  // next: Lines thick/thin at 103
    keywords: ['banding', 'horizontal lines', 'stripes', 'streaks'],
    naturalQueries: [
      'lines on print',
      'stripes across page',
      'horizontal stripes',
      'banding problem',
      'streaks on paper'
    ],
    description: 'How to fix horizontal lines or banding in prints'
  },
  {
    id: 't600-lines-thick-thin',
    title: 'Lines are Too Thick, Too Thin or Missing',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 103,
    endPage: 103,  // next: Lines stepped at 103 (same page)
    keywords: ['line thickness', 'thin lines', 'thick lines', 'missing lines'],
    naturalQueries: [
      'lines wrong thickness',
      'lines too thin',
      'lines too thick',
      'lines missing'
    ],
    description: 'Troubleshooting line thickness issues'
  },
  {
    id: 't600-lines-jagged',
    title: 'Lines Appear Stepped or Jagged',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 103,
    endPage: 104,  // next: Lines double at 104
    keywords: ['jagged', 'stepped', 'pixelated', 'rough edges'],
    naturalQueries: [
      'jagged lines',
      'not smooth',
      'pixelated print',
      'rough edges'
    ],
    description: 'How to fix jagged or stepped lines'
  },
  {
    id: 't600-blurry-grainy',
    title: 'Whole Image is Blurry or Grainy',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 105,
    endPage: 106,  // next: Paper not flat at 106
    keywords: ['blurry', 'grainy', 'fuzzy', 'not sharp', 'out of focus'],
    naturalQueries: [
      'print blurry',
      'image grainy',
      'not sharp',
      'fuzzy print',
      'poor quality'
    ],
    description: 'How to fix blurry or grainy prints'
  },
  {
    id: 't600-paper-not-flat',
    title: 'Paper is Not Flat',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 106,
    endPage: 107,  // next: Scuffed at 107
    keywords: ['wavy', 'curled', 'not flat', 'warped', 'cockled'],
    naturalQueries: [
      'paper curling',
      'wavy paper',
      'paper warped',
      'print not flat'
    ],
    description: 'How to fix paper flatness issues'
  },
  {
    id: 't600-scuffed-scratched',
    title: 'Print is Scuffed or Scratched',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 107,
    endPage: 108,  // next: Ink marks at 108
    keywords: ['scuff', 'scratch', 'marks', 'damage'],
    naturalQueries: [
      'scratches on print',
      'scuff marks',
      'print damaged',
      'marks on paper'
    ],
    description: 'How to fix scuffed or scratched prints'
  },
  {
    id: 't600-ink-marks',
    title: 'Ink Marks on the Paper',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 108,
    endPage: 109,  // next: Edges stepped at 109
    keywords: ['ink marks', 'smudge', 'ink stain', 'ink spots'],
    naturalQueries: [
      'ink smudges',
      'ink stains',
      'ink spots',
      'random ink marks'
    ],
    description: 'How to fix ink marks appearing on prints'
  },
  {
    id: 't600-colors-inaccurate',
    title: 'Colors are Inaccurate',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 109,
    endPage: 110,  // next: Colors fading at 110
    keywords: ['color', 'inaccurate', 'wrong color', 'color shift'],
    naturalQueries: [
      'wrong colors',
      'colors don\'t match',
      'color off',
      'colors look different'
    ],
    description: 'How to fix color accuracy issues'
  },
  {
    id: 't600-colors-fading',
    title: 'Colors are Fading',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 110,
    endPage: 110,  // next: Image incomplete at 110 (same page)
    keywords: ['fading', 'faded', 'pale', 'washed out'],
    naturalQueries: [
      'colors faded',
      'print too light',
      'washed out',
      'pale colors'
    ],
    description: 'How to fix fading or washed out colors'
  },
  {
    id: 't600-image-clipped',
    title: 'Image is Clipped',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 110,
    endPage: 111,  // next: Missing objects at 111
    keywords: ['clipped', 'cut off', 'cropped', 'missing parts'],
    naturalQueries: [
      'image cut off',
      'print missing parts',
      'edges cut off',
      'not all printing'
    ],
    description: 'How to fix clipped or cut off images'
  },

  // ============ TROUBLESHOOTING - GENERAL ============
  {
    id: 't600-wont-start',
    title: 'Printer Does Not Start',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 123,
    endPage: 123,  // next: Won't print at 123 (same page)
    keywords: ['won\'t start', 'no power', 'dead', 'not turning on'],
    naturalQueries: [
      'printer won\'t turn on',
      'no power',
      'printer dead',
      'won\'t start'
    ],
    description: 'Troubleshooting when printer won\'t start'
  },
  {
    id: 't600-wont-print',
    title: 'Printer Does Not Print',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 123,
    endPage: 123,  // next: Slow at 123 (same page)
    keywords: ['won\'t print', 'not printing', 'no output', 'print job stuck'],
    naturalQueries: [
      'printer won\'t print',
      'nothing printing',
      'print job stuck',
      'sent to printer but nothing'
    ],
    description: 'Troubleshooting when printer won\'t print'
  },
  {
    id: 't600-slow',
    title: 'Printer Seems Slow',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 123,
    endPage: 124,  // next: Control panel frozen at 124
    keywords: ['slow', 'slow printing', 'takes long', 'sluggish'],
    naturalQueries: [
      'printer slow',
      'printing takes forever',
      'too slow',
      'speed problem'
    ],
    description: 'How to fix slow printing issues'
  },
  {
    id: 't600-control-panel-frozen',
    title: 'Control Panel is Frozen',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 124,
    endPage: 125,  // next: Restore factory at 125
    keywords: ['frozen', 'stuck', 'unresponsive', 'not responding'],
    naturalQueries: [
      'screen frozen',
      'panel not responding',
      'stuck on screen',
      'touchscreen not working'
    ],
    description: 'How to fix a frozen control panel'
  },
  {
    id: 't600-factory-reset',
    title: 'Restore Factory Defaults and Settings',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 125,
    endPage: 127,  // next: Chapter 16 Error messages at 127
    keywords: ['reset', 'factory reset', 'restore', 'defaults'],
    naturalQueries: [
      'reset printer',
      'factory reset',
      'start fresh',
      'clear settings'
    ],
    description: 'How to restore the printer to factory defaults'
  },
  {
    id: 't600-error-messages',
    title: 'Control Panel Error Messages',
    category: 'troubleshooting',
    printers: ['T210', 'T230', 'T250', 'T630', 'T650'],
    manual: 't600',
    page: 127,
    endPage: 129,  // next: Chapter 17 HP Customer Care at 129
    keywords: ['error', 'error message', 'error code', 'warning'],
    naturalQueries: [
      'error on screen',
      'what does error mean',
      'error code',
      'warning message'
    ],
    description: 'Understanding control panel error messages'
  },

  // ============ T850/T950 SERIES ============
  // ============ SETUP ============
  {
    id: 't850-turn-on-off',
    title: 'Turn the Printer On and Off',
    category: 'setup',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 8,
    endPage: 9,
    keywords: ['power', 'on', 'off', 'start', 'shutdown', 'turn on', 'turn off'],
    naturalQueries: ['how to start printer', 'printer won\'t turn on', 'where is power button', 'shut down printer'],
    description: 'How to properly turn the printer on and off'
  },
  {
    id: 't850-control-panel',
    title: 'Control Panel',
    category: 'setup',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 9,
    endPage: 11,
    keywords: ['control panel', 'screen', 'display', 'buttons', 'menu', 'interface'],
    naturalQueries: ['how to use screen', 'what do buttons do', 'navigate menu', 'front panel'],
    description: 'Understanding the printer control panel and menus'
  },
  {
    id: 't850-connect-network',
    title: 'Connect the Printer to Your Network',
    category: 'setup',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 17,
    endPage: 18,
    keywords: ['network', 'wifi', 'wireless', 'ethernet', 'connect', 'internet', 'lan'],
    naturalQueries: ['connect to wifi', 'setup wireless', 'printer not connecting', 'network setup'],
    description: 'How to connect the printer to your wired or wireless network'
  },
  {
    id: 't850-configure-network',
    title: 'Configure Network Settings',
    category: 'setup',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 22,
    endPage: 23,
    keywords: ['network settings', 'ip address', 'dns', 'dhcp', 'static ip'],
    naturalQueries: ['change ip address', 'network configuration', 'ip settings'],
    description: 'Advanced network configuration options'
  },

  // ============ T850 PAPER HANDLING ============
  {
    id: 't850-load-roll-spindle',
    title: 'Load a Roll onto the Spindle',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 25,
    endPage: 27,
    keywords: ['roll', 'spindle', 'load paper', 'paper roll', 'install roll'],
    naturalQueries: ['put paper roll in', 'change paper roll', 'new roll of paper', 'install paper'],
    description: 'How to load a paper roll onto the spindle'
  },
  {
    id: 't850-load-roll-printer',
    title: 'Load a Roll into the Printer',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 27,
    endPage: 28,
    keywords: ['load roll', 'insert roll', 'feed paper', 'roll paper'],
    naturalQueries: ['put roll in printer', 'feed paper roll', 'paper not feeding'],
    description: 'How to load the paper roll into the printer'
  },
  {
    id: 't850-unload-roll',
    title: 'Unload a Roll',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 28,
    endPage: 28,
    keywords: ['unload', 'remove roll', 'take out paper', 'eject roll'],
    naturalQueries: ['remove paper roll', 'take out roll', 'change paper'],
    description: 'How to remove a paper roll from the printer'
  },
  {
    id: 't850-use-tray',
    title: 'Use the Tray',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 28,
    endPage: 30,
    keywords: ['tray', 'paper tray', 'sheet feeder', 'multi-sheet'],
    naturalQueries: ['use paper tray', 'load tray', 'multiple sheets'],
    description: 'How to use the paper tray'
  },
  {
    id: 't850-load-single-sheet',
    title: 'Load a Single Sheet',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 30,
    endPage: 31,
    keywords: ['single sheet', 'sheet paper', 'manual feed', 'one page'],
    naturalQueries: ['print on one sheet', 'load single page', 'use sheet paper'],
    description: 'How to load a single sheet of paper'
  },
  {
    id: 't850-change-drying-time',
    title: 'Change the Drying Time',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 35,
    endPage: 35,
    keywords: ['drying', 'dry time', 'ink dry', 'smudge'],
    naturalQueries: ['ink smudging', 'print wet', 'ink not dry', 'smearing'],
    description: 'How to adjust drying time for different paper types'
  },
  {
    id: 't850-cutter-on-off',
    title: 'Turn the Automatic Cutter On and Off',
    category: 'paper',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 35,
    endPage: 36,
    keywords: ['cutter', 'auto cut', 'paper cutter', 'cut paper'],
    naturalQueries: ['paper not cutting', 'disable cutter', 'auto cut off'],
    description: 'How to enable or disable the automatic paper cutter'
  },

  // ============ T850 PRINTING ============
  {
    id: 't850-hp-click',
    title: 'Easy Printing with HP Click',
    category: 'printing',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 37,
    endPage: 37,
    keywords: ['hp click', 'easy print', 'simple print', 'hp software'],
    naturalQueries: ['easiest way to print', 'hp click software', 'simple printing'],
    description: 'Using HP Click for easy one-click printing'
  },
  {
    id: 't850-print-driver',
    title: 'Printing from an Application Using a Printer Driver',
    category: 'printing',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 37,
    endPage: 42,
    keywords: ['driver', 'print driver', 'application', 'software'],
    naturalQueries: ['print from computer', 'install driver', 'print settings'],
    description: 'How to print from applications using the printer driver'
  },
  {
    id: 't850-print-usb',
    title: 'Print from USB Flash Drive',
    category: 'printing',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 42,
    endPage: 42,
    keywords: ['usb', 'flash drive', 'thumb drive', 'usb stick'],
    naturalQueries: ['print from usb', 'usb printing', 'plug in usb'],
    description: 'How to print directly from a USB flash drive'
  },
  {
    id: 't850-wifi-direct',
    title: 'Print using Wi-Fi Direct',
    category: 'printing',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 42,
    endPage: 44,
    keywords: ['wifi direct', 'wireless print', 'direct connect', 'no network'],
    naturalQueries: ['print without network', 'direct wireless', 'connect phone to printer'],
    description: 'How to print using Wi-Fi Direct without a network'
  },
  {
    id: 't850-mobile-printing',
    title: 'Mobile Printing',
    category: 'printing',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 44,
    endPage: 49,
    keywords: ['mobile', 'phone', 'tablet', 'iphone', 'android', 'hp smart'],
    naturalQueries: ['print from phone', 'print from tablet', 'mobile app'],
    description: 'How to print from mobile devices'
  },
  {
    id: 't850-print-correct-colors',
    title: 'Print a Document with the Correct Colors',
    category: 'printing',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 75,
    endPage: 79,
    keywords: ['color', 'colors', 'color management', 'accurate colors', 'color profile'],
    naturalQueries: ['colors look wrong', 'colors don\'t match', 'wrong colors', 'fix colors'],
    description: 'How to ensure your prints have accurate colors'
  },

  // ============ T850 MAINTENANCE ============
  {
    id: 't850-check-ink-status',
    title: 'Check Ink Cartridge Status',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 85,
    endPage: 85,
    keywords: ['ink level', 'ink status', 'cartridge level', 'how much ink'],
    naturalQueries: ['how much ink left', 'check ink', 'ink levels', 'running low on ink'],
    description: 'How to check the ink levels in your cartridges'
  },
  {
    id: 't850-replace-ink-cartridge',
    title: 'Replace an Ink Cartridge',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 85,
    endPage: 86,
    keywords: ['replace ink', 'change ink', 'new cartridge', 'ink cartridge', 'install ink'],
    naturalQueries: ['ink empty', 'change ink', 'put in new ink', 'out of ink'],
    description: 'Step-by-step guide to replace ink cartridges'
  },
  {
    id: 't850-about-printhead',
    title: 'About the Printhead',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 86,
    endPage: 87,
    keywords: ['printhead', 'print head', 'head info'],
    naturalQueries: ['what is printhead', 'printhead information'],
    description: 'Information about the printhead component'
  },
  {
    id: 't850-print-quality-diagnostic',
    title: 'Print Quality Diagnostic Report',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 87,
    endPage: 88,
    keywords: ['diagnostic', 'quality report', 'test print', 'print test'],
    naturalQueries: ['test print quality', 'print diagnostic', 'quality check'],
    description: 'How to run a print quality diagnostic report'
  },
  {
    id: 't850-clean-printhead',
    title: 'Clean the Printhead',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 88,
    endPage: 89,
    keywords: ['clean printhead', 'printhead cleaning', 'head clean', 'nozzle clean'],
    naturalQueries: ['streaks on print', 'lines on paper', 'poor print quality', 'banding'],
    description: 'How to clean the printhead to fix print quality issues'
  },
  {
    id: 't850-align-printhead',
    title: 'Align the Printhead',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 89,
    endPage: 89,
    keywords: ['align', 'alignment', 'printhead alignment', 'calibrate'],
    naturalQueries: ['print not straight', 'text blurry', 'lines not aligned'],
    description: 'How to align the printhead for better print quality'
  },
  {
    id: 't850-replace-printhead',
    title: 'Replace the Printhead',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 89,
    endPage: 92,
    keywords: ['replace printhead', 'new printhead', 'change printhead', 'printhead replacement'],
    naturalQueries: ['printhead broken', 'printhead error', 'need new printhead'],
    description: 'Step-by-step guide to replace the printhead'
  },
  {
    id: 't850-clean-encoder-strip',
    title: 'Clean the Encoder Strip',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 93,
    endPage: 93,
    keywords: ['encoder strip', 'encoder', 'strip cleaning'],
    naturalQueries: ['weird lines', 'print position wrong', 'carriage errors'],
    description: 'How to clean the encoder strip'
  },
  {
    id: 't850-recalibrate-paper',
    title: 'Recalibrate the Paper Advance',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 93,
    endPage: 94,
    keywords: ['calibrate', 'paper advance', 'recalibrate', 'paper feed'],
    naturalQueries: ['paper feeding wrong', 'spacing issues', 'paper advance problem'],
    description: 'How to recalibrate the paper advance mechanism'
  },
  {
    id: 't850-move-store-printer',
    title: 'Move or Store the Printer',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 97,
    endPage: 97,
    keywords: ['move', 'transport', 'store', 'relocate', 'storage'],
    naturalQueries: ['moving printer', 'transport printer', 'store printer'],
    description: 'How to safely move or store the printer'
  },
  {
    id: 't850-firmware-update',
    title: 'Firmware Update',
    category: 'maintenance',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 97,
    endPage: 98,
    keywords: ['firmware', 'update', 'upgrade', 'software update'],
    naturalQueries: ['update printer', 'new firmware', 'printer update'],
    description: 'How to update the printer firmware'
  },

  // ============ T850 TROUBLESHOOTING ============
  {
    id: 't850-paper-load-problem',
    title: 'Paper Cannot be Loaded Successfully',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 103,
    endPage: 105,
    keywords: ['paper load', 'won\'t load', 'paper problem', 'loading error'],
    naturalQueries: ['paper won\'t load', 'can\'t load paper', 'paper not going in'],
    description: 'Troubleshooting paper loading issues'
  },
  {
    id: 't850-roll-jam',
    title: 'Roll has Jammed',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 107,
    endPage: 108,
    keywords: ['jam', 'paper jam', 'stuck', 'jammed', 'paper stuck'],
    naturalQueries: ['paper stuck', 'paper jam', 'clear jam', 'jammed paper'],
    description: 'How to clear a paper jam from the roll'
  },
  {
    id: 't850-tray-jam',
    title: 'Tray has Jammed',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 108,
    endPage: 111,
    keywords: ['tray jam', 'sheet jam', 'tray stuck'],
    naturalQueries: ['tray jammed', 'sheet stuck', 'tray paper jam'],
    description: 'How to clear a jam in the tray'
  },
  {
    id: 't850-banding',
    title: 'Horizontal Lines Across the Image (Banding)',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 120,
    endPage: 121,
    keywords: ['banding', 'horizontal lines', 'stripes', 'streaks'],
    naturalQueries: ['lines on print', 'stripes across page', 'horizontal stripes', 'banding problem'],
    description: 'How to fix horizontal lines or banding in prints'
  },
  {
    id: 't850-blurry-grainy',
    title: 'Whole Image is Blurry or Grainy',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 124,
    endPage: 124,
    keywords: ['blurry', 'grainy', 'fuzzy', 'not sharp', 'out of focus'],
    naturalQueries: ['print blurry', 'image grainy', 'not sharp', 'poor quality'],
    description: 'How to fix blurry or grainy prints'
  },
  {
    id: 't850-colors-inaccurate',
    title: 'Colors are Inaccurate',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 127,
    endPage: 128,
    keywords: ['color', 'inaccurate', 'wrong color', 'color shift'],
    naturalQueries: ['wrong colors', 'colors don\'t match', 'color off'],
    description: 'How to fix color accuracy issues'
  },
  {
    id: 't850-wont-start',
    title: 'Printer Does Not Start',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 164,
    endPage: 164,
    keywords: ['won\'t start', 'no power', 'dead', 'not turning on'],
    naturalQueries: ['printer won\'t turn on', 'no power', 'printer dead'],
    description: 'Troubleshooting when printer won\'t start'
  },
  {
    id: 't850-wont-print',
    title: 'Printer Does Not Print',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 164,
    endPage: 164,
    keywords: ['won\'t print', 'not printing', 'no output', 'print job stuck'],
    naturalQueries: ['printer won\'t print', 'nothing printing', 'print job stuck'],
    description: 'Troubleshooting when printer won\'t print'
  },
  {
    id: 't850-control-panel-frozen',
    title: 'Control Panel is Frozen',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 165,
    endPage: 165,
    keywords: ['frozen', 'stuck', 'unresponsive', 'not responding'],
    naturalQueries: ['screen frozen', 'panel not responding', 'stuck on screen'],
    description: 'How to fix a frozen control panel'
  },
  {
    id: 't850-factory-reset',
    title: 'Restore Factory Defaults and Settings',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 165,
    endPage: 166,
    keywords: ['reset', 'factory reset', 'restore', 'defaults'],
    naturalQueries: ['reset printer', 'factory reset', 'start fresh'],
    description: 'How to restore the printer to factory defaults'
  },
  {
    id: 't850-error-messages',
    title: 'Control Panel Error Messages',
    category: 'troubleshooting',
    printers: ['T850', 'T850 MFP', 'T950', 'T950 MFP'],
    manual: 't850',
    page: 167,
    endPage: 172,
    keywords: ['error', 'error message', 'error code', 'warning'],
    naturalQueries: ['error on screen', 'what does error mean', 'error code'],
    description: 'Understanding control panel error messages'
  },

  // ============ Z6/Z9+ SERIES ============
  // ============ SETUP ============
  {
    id: 'z6-turn-on-off',
    title: 'Turn the Printer On and Off',
    category: 'setup',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 12,
    endPage: 14,
    keywords: ['power', 'on', 'off', 'start', 'shutdown', 'turn on', 'turn off'],
    naturalQueries: ['how to start printer', 'printer won\'t turn on', 'where is power button', 'shut down printer'],
    description: 'How to properly turn the printer on and off'
  },
  {
    id: 'z6-front-panel',
    title: 'Front Panel',
    category: 'setup',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 14,
    endPage: 17,
    keywords: ['front panel', 'control panel', 'screen', 'display', 'buttons', 'menu'],
    naturalQueries: ['how to use screen', 'what do buttons do', 'navigate menu', 'touchscreen'],
    description: 'Understanding the printer front panel and menus'
  },
  {
    id: 'z6-embedded-web-server',
    title: 'Embedded Web Server',
    category: 'setup',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 18,
    endPage: 19,
    keywords: ['embedded web server', 'ews', 'web interface', 'browser'],
    naturalQueries: ['access printer from browser', 'web server', 'remote access'],
    description: 'How to access and use the Embedded Web Server'
  },
  {
    id: 'z6-configure-printer',
    title: 'Configure Your Printer',
    category: 'setup',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 20,
    endPage: 28,
    keywords: ['configure', 'setup', 'settings', 'network', 'wifi'],
    naturalQueries: ['printer settings', 'setup printer', 'configure network'],
    description: 'How to configure printer settings'
  },

  // ============ Z6 PAPER HANDLING ============
  {
    id: 'z6-load-roll-spindle',
    title: 'Load a Roll onto the Spindle',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 31,
    endPage: 34,
    keywords: ['roll', 'spindle', 'load paper', 'paper roll', 'install roll'],
    naturalQueries: ['put paper roll in', 'change paper roll', 'new roll of paper', 'install paper'],
    description: 'How to load a paper roll onto the spindle'
  },
  {
    id: 'z6-load-roll-printer',
    title: 'Load a Roll into the Printer',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 34,
    endPage: 37,
    keywords: ['load roll', 'insert roll', 'feed paper', 'roll paper'],
    naturalQueries: ['put roll in printer', 'feed paper roll', 'paper not feeding'],
    description: 'How to load the paper roll into the printer'
  },
  {
    id: 'z6-unload-roll',
    title: 'Unload a Roll',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 37,
    endPage: 38,
    keywords: ['unload', 'remove roll', 'take out paper', 'eject roll'],
    naturalQueries: ['remove paper roll', 'take out roll', 'change paper'],
    description: 'How to remove a paper roll from the printer'
  },
  {
    id: 'z6-load-single-sheet',
    title: 'Load a Single Sheet',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 38,
    endPage: 41,
    keywords: ['single sheet', 'sheet paper', 'manual feed', 'one page'],
    naturalQueries: ['print on one sheet', 'load single page', 'use sheet paper'],
    description: 'How to load a single sheet of paper'
  },
  {
    id: 'z6-paper-presets',
    title: 'Paper Presets',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 43,
    endPage: 45,
    keywords: ['paper preset', 'paper profile', 'paper type', 'media preset'],
    naturalQueries: ['add paper type', 'paper profile', 'custom paper'],
    description: 'How to use and create paper presets'
  },
  {
    id: 'z6-change-drying-time',
    title: 'Change the Drying Time',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 47,
    endPage: 47,
    keywords: ['drying', 'dry time', 'ink dry', 'smudge'],
    naturalQueries: ['ink smudging', 'print wet', 'ink not dry', 'smearing'],
    description: 'How to adjust drying time for different paper types'
  },
  {
    id: 'z6-cutter-on-off',
    title: 'Turn the Automatic Horizontal Cutter On and Off',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 47,
    endPage: 48,
    keywords: ['cutter', 'auto cut', 'paper cutter', 'cut paper', 'horizontal cutter'],
    naturalQueries: ['paper not cutting', 'disable cutter', 'auto cut off'],
    description: 'How to enable or disable the automatic paper cutter'
  },
  {
    id: 'z6-vertical-trimmer',
    title: 'Turn the Automatic Vertical Trimmer On and Off',
    category: 'paper',
    printers: ['Z6dr', 'Z9+dr'],
    manual: 'z6',
    page: 48,
    endPage: 49,
    keywords: ['vertical trimmer', 'trimmer', 'side cut', 'edge trim'],
    naturalQueries: ['trim edges', 'vertical cut', 'side trimmer'],
    description: 'How to enable or disable the vertical trimmer (dr models only)'
  },
  {
    id: 'z6-take-up-reel',
    title: 'Use the Take-up Reel',
    category: 'paper',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 49,
    endPage: 58,
    keywords: ['take-up reel', 'spindle', 'roll output', 'collect prints'],
    naturalQueries: ['collect prints on roll', 'take up reel', 'output spindle'],
    description: 'How to use the take-up reel for collecting prints'
  },

  // ============ Z6 PRINTING ============
  {
    id: 'z6-print-usb',
    title: 'Print from a USB Flash Drive',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 69,
    endPage: 71,
    keywords: ['usb', 'flash drive', 'thumb drive', 'usb stick'],
    naturalQueries: ['print from usb', 'usb printing', 'plug in usb'],
    description: 'How to print directly from a USB flash drive'
  },
  {
    id: 'z6-print-driver',
    title: 'Print from a Computer Using a Printer Driver',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 71,
    endPage: 72,
    keywords: ['driver', 'print driver', 'application', 'software'],
    naturalQueries: ['print from computer', 'install driver', 'print settings'],
    description: 'How to print from applications using the printer driver'
  },
  {
    id: 'z6-advanced-print-settings',
    title: 'Advanced Print Settings',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 72,
    endPage: 88,
    keywords: ['advanced', 'settings', 'quality', 'margins', 'nesting'],
    naturalQueries: ['print quality settings', 'advanced options', 'configure print'],
    description: 'Advanced print settings and options'
  },
  {
    id: 'z6-mobile-printing',
    title: 'Mobile Printing',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 88,
    endPage: 91,
    keywords: ['mobile', 'phone', 'tablet', 'iphone', 'android', 'hp smart', 'email'],
    naturalQueries: ['print from phone', 'print from tablet', 'mobile app', 'print by email'],
    description: 'How to print from mobile devices'
  },
  {
    id: 'z6-color-calibration',
    title: 'Color Calibration',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 102,
    endPage: 104,
    keywords: ['color calibration', 'calibrate', 'color accuracy', 'color match'],
    naturalQueries: ['calibrate colors', 'colors wrong', 'color accuracy'],
    description: 'How to calibrate colors for accurate prints'
  },
  {
    id: 'z6-color-profiling',
    title: 'Color Profiling',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 104,
    endPage: 108,
    keywords: ['color profile', 'icc profile', 'color management'],
    naturalQueries: ['create color profile', 'icc profile', 'color management'],
    description: 'How to create and use color profiles'
  },
  {
    id: 'z6-print-correct-colors',
    title: 'Print a Document with the Correct Colors',
    category: 'printing',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 120,
    endPage: 126,
    keywords: ['color', 'colors', 'color management', 'accurate colors'],
    naturalQueries: ['colors look wrong', 'colors don\'t match', 'wrong colors', 'fix colors'],
    description: 'How to ensure your prints have accurate colors'
  },

  // ============ Z6 MAINTENANCE ============
  {
    id: 'z6-check-ink-status',
    title: 'Check the Ink Cartridges Status',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 150,
    endPage: 150,
    keywords: ['ink level', 'ink status', 'cartridge level', 'how much ink'],
    naturalQueries: ['how much ink left', 'check ink', 'ink levels', 'running low on ink'],
    description: 'How to check the ink levels in your cartridges'
  },
  {
    id: 'z6-remove-ink-cartridge',
    title: 'Remove an Ink Cartridge',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 150,
    endPage: 152,
    keywords: ['remove ink', 'take out ink', 'ink cartridge'],
    naturalQueries: ['remove ink cartridge', 'take out ink'],
    description: 'How to remove an ink cartridge'
  },
  {
    id: 'z6-insert-ink-cartridge',
    title: 'Insert an Ink Cartridge',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 152,
    endPage: 153,
    keywords: ['insert ink', 'install ink', 'new cartridge', 'replace ink'],
    naturalQueries: ['put in new ink', 'install ink cartridge', 'change ink'],
    description: 'How to insert a new ink cartridge'
  },
  {
    id: 'z6-check-printhead-status',
    title: 'Check the Printhead Status',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 153,
    endPage: 154,
    keywords: ['printhead status', 'printhead info', 'head status'],
    naturalQueries: ['check printhead', 'printhead condition'],
    description: 'How to check the status of the printheads'
  },
  {
    id: 'z6-remove-printhead',
    title: 'Remove a Printhead',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 154,
    endPage: 156,
    keywords: ['remove printhead', 'take out printhead'],
    naturalQueries: ['remove printhead', 'take out print head'],
    description: 'How to remove a printhead'
  },
  {
    id: 'z6-insert-printhead',
    title: 'Insert a Printhead',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 156,
    endPage: 159,
    keywords: ['insert printhead', 'install printhead', 'replace printhead'],
    naturalQueries: ['install new printhead', 'put in printhead', 'replace printhead'],
    description: 'How to insert a new printhead'
  },
  {
    id: 'z6-move-store-printer',
    title: 'Move or Store the Printer',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 162,
    endPage: 163,
    keywords: ['move', 'transport', 'store', 'relocate', 'storage'],
    naturalQueries: ['moving printer', 'transport printer', 'store printer'],
    description: 'How to safely move or store the printer'
  },
  {
    id: 'z6-firmware-update',
    title: 'Update the Firmware',
    category: 'maintenance',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 163,
    endPage: 164,
    keywords: ['firmware', 'update', 'upgrade'],
    naturalQueries: ['update printer', 'new firmware', 'printer update'],
    description: 'How to update the printer firmware'
  },

  // ============ Z6 TROUBLESHOOTING ============
  {
    id: 'z6-paper-load-problem',
    title: 'Paper Cannot be Loaded Successfully',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 174,
    endPage: 176,
    keywords: ['paper load', 'won\'t load', 'paper problem', 'loading error'],
    naturalQueries: ['paper won\'t load', 'can\'t load paper', 'paper not going in'],
    description: 'Troubleshooting paper loading issues'
  },
  {
    id: 'z6-paper-jam',
    title: 'Paper has Jammed',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 180,
    endPage: 183,
    keywords: ['jam', 'paper jam', 'stuck', 'jammed', 'paper stuck'],
    naturalQueries: ['paper stuck', 'paper jam', 'clear jam', 'jammed paper'],
    description: 'How to clear a paper jam'
  },
  {
    id: 'z6-recalibrate-paper',
    title: 'Recalibrate the Paper Advance',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 190,
    endPage: 192,
    keywords: ['calibrate', 'paper advance', 'recalibrate', 'paper feed'],
    naturalQueries: ['paper feeding wrong', 'spacing issues', 'banding fix'],
    description: 'How to recalibrate the paper advance mechanism'
  },
  {
    id: 'z6-banding',
    title: 'Horizontal Lines Across the Image (Banding)',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 192,
    endPage: 193,
    keywords: ['banding', 'horizontal lines', 'stripes', 'streaks'],
    naturalQueries: ['lines on print', 'stripes across page', 'horizontal stripes', 'banding problem'],
    description: 'How to fix horizontal lines or banding in prints'
  },
  {
    id: 'z6-blurry-grainy',
    title: 'Whole Image is Blurry or Grainy',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 194,
    endPage: 194,
    keywords: ['blurry', 'grainy', 'fuzzy', 'not sharp', 'out of focus'],
    naturalQueries: ['print blurry', 'image grainy', 'not sharp', 'poor quality'],
    description: 'How to fix blurry or grainy prints'
  },
  {
    id: 'z6-scuffed-scratched',
    title: 'Print is Scuffed or Scratched',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 195,
    endPage: 197,
    keywords: ['scuff', 'scratch', 'marks', 'damage'],
    naturalQueries: ['scratches on print', 'scuff marks', 'print damaged'],
    description: 'How to fix scuffed or scratched prints'
  },
  {
    id: 'z6-colors-inaccurate',
    title: 'Colors are Inaccurate',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 199,
    endPage: 201,
    keywords: ['color', 'inaccurate', 'wrong color', 'color shift'],
    naturalQueries: ['wrong colors', 'colors don\'t match', 'color off'],
    description: 'How to fix color accuracy issues'
  },
  {
    id: 'z6-image-diagnostics',
    title: 'Image Diagnostics Print',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 207,
    endPage: 209,
    keywords: ['diagnostic', 'test print', 'image diagnostics'],
    naturalQueries: ['run diagnostic', 'test print quality', 'diagnose problem'],
    description: 'How to use the Image Diagnostics Print'
  },
  {
    id: 'z6-clean-printheads',
    title: 'Clean the Printheads',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 212,
    endPage: 212,
    keywords: ['clean printhead', 'printhead cleaning', 'head clean', 'nozzle clean'],
    naturalQueries: ['streaks on print', 'lines on paper', 'poor print quality'],
    description: 'How to clean the printheads to fix print quality issues'
  },
  {
    id: 'z6-align-printheads',
    title: 'Align the Printheads',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 216,
    endPage: 217,
    keywords: ['align', 'alignment', 'printhead alignment', 'calibrate'],
    naturalQueries: ['print not straight', 'text blurry', 'lines not aligned'],
    description: 'How to align the printheads for better print quality'
  },
  {
    id: 'z6-wont-print',
    title: 'Printer Does Not Print',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 219,
    endPage: 219,
    keywords: ['won\'t print', 'not printing', 'no output', 'print job stuck'],
    naturalQueries: ['printer won\'t print', 'nothing printing', 'print job stuck'],
    description: 'Troubleshooting when printer won\'t print'
  },
  {
    id: 'z6-slow',
    title: 'Printer Seems Slow',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 220,
    endPage: 220,
    keywords: ['slow', 'slow printing', 'takes long', 'sluggish'],
    naturalQueries: ['printer slow', 'printing takes forever', 'too slow'],
    description: 'How to fix slow printing issues'
  },
  {
    id: 'z6-error-messages',
    title: 'Front-Panel Error Messages',
    category: 'troubleshooting',
    printers: ['Z6', 'Z6dr', 'Z9+', 'Z9+dr'],
    manual: 'z6',
    page: 223,
    endPage: 224,
    keywords: ['error', 'error message', 'error code', 'warning'],
    naturalQueries: ['error on screen', 'what does error mean', 'error code'],
    description: 'Understanding front-panel error messages'
  },

  // ============ T870 SERIES ============
  // ============ SETUP ============
  {
    id: 't870-turn-on-off',
    title: 'Turn the Printer On and Off',
    category: 'setup',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 7,
    endPage: 7,
    keywords: ['power', 'on', 'off', 'start', 'shutdown', 'turn on', 'turn off'],
    naturalQueries: ['how to start printer', 'printer won\'t turn on', 'where is power button', 'shut down printer'],
    description: 'How to properly turn the printer on and off'
  },
  {
    id: 't870-control-panel',
    title: 'Control Panel',
    category: 'setup',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 7,
    endPage: 8,
    keywords: ['control panel', 'screen', 'display', 'buttons', 'menu', 'interface'],
    naturalQueries: ['how to use screen', 'what do buttons do', 'navigate menu', 'front panel'],
    description: 'Understanding the printer control panel and menus'
  },
  {
    id: 't870-connect-network',
    title: 'Connect the Printer to Your Network',
    category: 'setup',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 14,
    endPage: 15,
    keywords: ['network', 'wifi', 'wireless', 'ethernet', 'connect', 'internet', 'lan'],
    naturalQueries: ['connect to wifi', 'setup wireless', 'printer not connecting', 'network setup'],
    description: 'How to connect the printer to your wired or wireless network'
  },
  {
    id: 't870-embedded-web-server',
    title: 'Access the Embedded Web Server',
    category: 'setup',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 15,
    endPage: 16,
    keywords: ['embedded web server', 'ews', 'web interface', 'browser'],
    naturalQueries: ['access printer from browser', 'web server', 'remote access'],
    description: 'How to access and use the Embedded Web Server'
  },
  {
    id: 't870-configure-network',
    title: 'Configure Network Settings',
    category: 'setup',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 19,
    endPage: 20,
    keywords: ['network settings', 'ip address', 'dns', 'dhcp', 'static ip'],
    naturalQueries: ['change ip address', 'network configuration', 'ip settings'],
    description: 'Advanced network configuration options'
  },

  // ============ T870 PAPER HANDLING ============
  {
    id: 't870-load-roll-spindle',
    title: 'Load a Roll onto the Spindle',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 23,
    endPage: 25,
    keywords: ['roll', 'spindle', 'load paper', 'paper roll', 'install roll'],
    naturalQueries: ['put paper roll in', 'change paper roll', 'new roll of paper', 'install paper'],
    description: 'How to load a paper roll onto the spindle'
  },
  {
    id: 't870-load-roll-printer',
    title: 'Load a Roll into the Printer',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 25,
    endPage: 26,
    keywords: ['load roll', 'insert roll', 'feed paper', 'roll paper'],
    naturalQueries: ['put roll in printer', 'feed paper roll', 'paper not feeding'],
    description: 'How to load the paper roll into the printer'
  },
  {
    id: 't870-unload-roll',
    title: 'Unload a Roll',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 26,
    endPage: 26,
    keywords: ['unload', 'remove roll', 'take out paper', 'eject roll'],
    naturalQueries: ['remove paper roll', 'take out roll', 'change paper'],
    description: 'How to remove a paper roll from the printer'
  },
  {
    id: 't870-use-tray',
    title: 'Use the Tray',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 26,
    endPage: 27,
    keywords: ['tray', 'paper tray', 'sheet feeder', 'multi-sheet'],
    naturalQueries: ['use paper tray', 'load tray', 'multiple sheets'],
    description: 'How to use the paper tray'
  },
  {
    id: 't870-load-single-sheet',
    title: 'Load a Single Sheet',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 27,
    endPage: 28,
    keywords: ['single sheet', 'sheet paper', 'manual feed', 'one page'],
    naturalQueries: ['print on one sheet', 'load single page', 'use sheet paper'],
    description: 'How to load a single sheet of paper'
  },
  {
    id: 't870-sheet-organizer',
    title: 'Use the Sheet Organizer Accessory',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 28,
    endPage: 29,
    keywords: ['sheet organizer', 'organizer', 'accessory', 'output tray'],
    naturalQueries: ['sheet organizer', 'organize prints', 'output tray'],
    description: 'How to use the sheet organizer accessory'
  },
  {
    id: 't870-change-drying-time',
    title: 'Change the Drying Time',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 30,
    endPage: 30,
    keywords: ['drying', 'dry time', 'ink dry', 'smudge'],
    naturalQueries: ['ink smudging', 'print wet', 'ink not dry', 'smearing'],
    description: 'How to adjust drying time for different paper types'
  },
  {
    id: 't870-cutter-on-off',
    title: 'Turn the Automatic Cutter On and Off',
    category: 'paper',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 30,
    endPage: 31,
    keywords: ['cutter', 'auto cut', 'paper cutter', 'cut paper'],
    naturalQueries: ['paper not cutting', 'disable cutter', 'auto cut off'],
    description: 'How to enable or disable the automatic paper cutter'
  },

  // ============ T870 PRINTING ============
  {
    id: 't870-hp-click',
    title: 'Easy Printing with HP Click',
    category: 'printing',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 33,
    endPage: 33,
    keywords: ['hp click', 'easy print', 'simple print', 'hp software'],
    naturalQueries: ['easiest way to print', 'hp click software', 'simple printing'],
    description: 'Using HP Click for easy one-click printing'
  },
  {
    id: 't870-print-driver',
    title: 'Printing from an Application Using a Printer Driver',
    category: 'printing',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 33,
    endPage: 38,
    keywords: ['driver', 'print driver', 'application', 'software'],
    naturalQueries: ['print from computer', 'install driver', 'print settings'],
    description: 'How to print from applications using the printer driver'
  },
  {
    id: 't870-print-usb',
    title: 'Print from USB Flash Drive',
    category: 'printing',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 38,
    endPage: 38,
    keywords: ['usb', 'flash drive', 'thumb drive', 'usb stick'],
    naturalQueries: ['print from usb', 'usb printing', 'plug in usb'],
    description: 'How to print directly from a USB flash drive'
  },
  {
    id: 't870-wifi-direct',
    title: 'Print using Wi-Fi Direct',
    category: 'printing',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 38,
    endPage: 40,
    keywords: ['wifi direct', 'wireless print', 'direct connect', 'no network'],
    naturalQueries: ['print without network', 'direct wireless', 'connect phone to printer'],
    description: 'How to print using Wi-Fi Direct without a network'
  },
  {
    id: 't870-mobile-printing',
    title: 'Mobile Printing',
    category: 'printing',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 40,
    endPage: 44,
    keywords: ['mobile', 'phone', 'tablet', 'iphone', 'android', 'hp smart'],
    naturalQueries: ['print from phone', 'print from tablet', 'mobile app'],
    description: 'How to print from mobile devices'
  },
  {
    id: 't870-print-correct-colors',
    title: 'Print a Document with the Correct Colors',
    category: 'printing',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 50,
    endPage: 54,
    keywords: ['color', 'colors', 'color management', 'accurate colors', 'color profile'],
    naturalQueries: ['colors look wrong', 'colors don\'t match', 'wrong colors', 'fix colors'],
    description: 'How to ensure your prints have accurate colors'
  },

  // ============ T870 MAINTENANCE ============
  {
    id: 't870-check-ink-status',
    title: 'Check Ink Cartridge Status',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 60,
    endPage: 60,
    keywords: ['ink level', 'ink status', 'cartridge level', 'how much ink'],
    naturalQueries: ['how much ink left', 'check ink', 'ink levels', 'running low on ink'],
    description: 'How to check the ink levels in your cartridges'
  },
  {
    id: 't870-replace-ink-cartridge',
    title: 'Replace an Ink Cartridge',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 60,
    endPage: 61,
    keywords: ['replace ink', 'change ink', 'new cartridge', 'ink cartridge', 'install ink'],
    naturalQueries: ['ink empty', 'change ink', 'put in new ink', 'out of ink'],
    description: 'Step-by-step guide to replace ink cartridges'
  },
  {
    id: 't870-about-printhead',
    title: 'About the Printhead',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 61,
    endPage: 62,
    keywords: ['printhead', 'print head', 'head info'],
    naturalQueries: ['what is printhead', 'printhead information'],
    description: 'Information about the printhead component'
  },
  {
    id: 't870-print-quality-diagnostic',
    title: 'Print Quality Diagnostic Report',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 62,
    endPage: 63,
    keywords: ['diagnostic', 'quality report', 'test print', 'print test'],
    naturalQueries: ['test print quality', 'print diagnostic', 'quality check'],
    description: 'How to run a print quality diagnostic report'
  },
  {
    id: 't870-clean-printhead',
    title: 'Clean the Printhead',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 63,
    endPage: 64,
    keywords: ['clean printhead', 'printhead cleaning', 'head clean', 'nozzle clean'],
    naturalQueries: ['streaks on print', 'lines on paper', 'poor print quality', 'banding'],
    description: 'How to clean the printhead to fix print quality issues'
  },
  {
    id: 't870-align-printhead',
    title: 'Align the Printhead',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 64,
    endPage: 64,
    keywords: ['align', 'alignment', 'printhead alignment', 'calibrate'],
    naturalQueries: ['print not straight', 'text blurry', 'lines not aligned'],
    description: 'How to align the printhead for better print quality'
  },
  {
    id: 't870-replace-printhead',
    title: 'Replace the Printhead',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 64,
    endPage: 67,
    keywords: ['replace printhead', 'new printhead', 'change printhead', 'printhead replacement'],
    naturalQueries: ['printhead broken', 'printhead error', 'need new printhead'],
    description: 'Step-by-step guide to replace the printhead'
  },
  {
    id: 't870-clean-encoder-strip',
    title: 'Clean the Encoder Strip',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 68,
    endPage: 68,
    keywords: ['encoder strip', 'encoder', 'strip cleaning'],
    naturalQueries: ['weird lines', 'print position wrong', 'carriage errors'],
    description: 'How to clean the encoder strip'
  },
  {
    id: 't870-recalibrate-paper',
    title: 'Recalibrate the Paper Advance',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 68,
    endPage: 69,
    keywords: ['calibrate', 'paper advance', 'recalibrate', 'paper feed'],
    naturalQueries: ['paper feeding wrong', 'spacing issues', 'paper advance problem'],
    description: 'How to recalibrate the paper advance mechanism'
  },
  {
    id: 't870-move-store-printer',
    title: 'Move or Store the Printer',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 69,
    endPage: 69,
    keywords: ['move', 'transport', 'store', 'relocate', 'storage'],
    naturalQueries: ['moving printer', 'transport printer', 'store printer'],
    description: 'How to safely move or store the printer'
  },
  {
    id: 't870-firmware-update',
    title: 'Firmware Update',
    category: 'maintenance',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 69,
    endPage: 70,
    keywords: ['firmware', 'update', 'upgrade', 'software update'],
    naturalQueries: ['update printer', 'new firmware', 'printer update'],
    description: 'How to update the printer firmware'
  },

  // ============ T870 TROUBLESHOOTING ============
  {
    id: 't870-paper-load-problem',
    title: 'Paper Cannot be Loaded Successfully',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 74,
    endPage: 76,
    keywords: ['paper load', 'won\'t load', 'paper problem', 'loading error'],
    naturalQueries: ['paper won\'t load', 'can\'t load paper', 'paper not going in'],
    description: 'Troubleshooting paper loading issues'
  },
  {
    id: 't870-roll-jam',
    title: 'Roll has Jammed',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 78,
    endPage: 79,
    keywords: ['jam', 'paper jam', 'stuck', 'jammed', 'paper stuck'],
    naturalQueries: ['paper stuck', 'paper jam', 'clear jam', 'jammed paper'],
    description: 'How to clear a paper jam from the roll'
  },
  {
    id: 't870-tray-jam',
    title: 'Tray has Jammed',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 79,
    endPage: 81,
    keywords: ['tray jam', 'sheet jam', 'tray stuck'],
    naturalQueries: ['tray jammed', 'sheet stuck', 'tray paper jam'],
    description: 'How to clear a jam in the tray'
  },
  {
    id: 't870-banding',
    title: 'Horizontal Lines Across the Image (Banding)',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 85,
    endPage: 86,
    keywords: ['banding', 'horizontal lines', 'stripes', 'streaks'],
    naturalQueries: ['lines on print', 'stripes across page', 'horizontal stripes', 'banding problem'],
    description: 'How to fix horizontal lines or banding in prints'
  },
  {
    id: 't870-blurry-grainy',
    title: 'Whole Image is Blurry or Grainy',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 89,
    endPage: 89,
    keywords: ['blurry', 'grainy', 'fuzzy', 'not sharp', 'out of focus'],
    naturalQueries: ['print blurry', 'image grainy', 'not sharp', 'poor quality'],
    description: 'How to fix blurry or grainy prints'
  },
  {
    id: 't870-colors-inaccurate',
    title: 'Colors are Inaccurate',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 92,
    endPage: 93,
    keywords: ['color', 'inaccurate', 'wrong color', 'color shift'],
    naturalQueries: ['wrong colors', 'colors don\'t match', 'color off'],
    description: 'How to fix color accuracy issues'
  },
  {
    id: 't870-wont-start',
    title: 'Printer Does Not Start',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 103,
    endPage: 103,
    keywords: ['won\'t start', 'no power', 'dead', 'not turning on'],
    naturalQueries: ['printer won\'t turn on', 'no power', 'printer dead'],
    description: 'Troubleshooting when printer won\'t start'
  },
  {
    id: 't870-wont-print',
    title: 'Printer Does Not Print',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 103,
    endPage: 103,
    keywords: ['won\'t print', 'not printing', 'no output', 'print job stuck'],
    naturalQueries: ['printer won\'t print', 'nothing printing', 'print job stuck'],
    description: 'Troubleshooting when printer won\'t print'
  },
  {
    id: 't870-slow',
    title: 'Printer Seems Slow',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 103,
    endPage: 104,
    keywords: ['slow', 'slow printing', 'takes long', 'sluggish'],
    naturalQueries: ['printer slow', 'printing takes forever', 'too slow'],
    description: 'How to fix slow printing issues'
  },
  {
    id: 't870-control-panel-frozen',
    title: 'Control Panel is Frozen',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 104,
    endPage: 104,
    keywords: ['frozen', 'stuck', 'unresponsive', 'not responding'],
    naturalQueries: ['screen frozen', 'panel not responding', 'stuck on screen'],
    description: 'How to fix a frozen control panel'
  },
  {
    id: 't870-factory-reset',
    title: 'Restore Factory Defaults and Settings',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 104,
    endPage: 105,
    keywords: ['reset', 'factory reset', 'restore', 'defaults'],
    naturalQueries: ['reset printer', 'factory reset', 'start fresh'],
    description: 'How to restore the printer to factory defaults'
  },
  {
    id: 't870-error-messages',
    title: 'Control Panel Error Messages',
    category: 'troubleshooting',
    printers: ['T870', 'T870 MFP'],
    manual: 't870',
    page: 106,
    endPage: 110,
    keywords: ['error', 'error message', 'error code', 'warning'],
    naturalQueries: ['error on screen', 'what does error mean', 'error code'],
    description: 'Understanding control panel error messages'
  }
];

// Fuse.js search configuration
// Printer lock (m11 #9): copied from app/src/data/resourcesMetadata.js (desktop usePrinterLock helpers).
export const allPrinterModels = [
  { model: 'HP DesignJet T210',     familyId: 't200', manualId: 't600', displayName: 'HP DesignJet T210' },
  { model: 'HP DesignJet T230',     familyId: 't200', manualId: 't600', displayName: 'HP DesignJet T230' },
  { model: 'HP DesignJet T250',     familyId: 't200', manualId: 't600', displayName: 'HP DesignJet T250' },
  { model: 'HP DesignJet T630',     familyId: 't600', manualId: 't600', displayName: 'HP DesignJet T630' },
  { model: 'HP DesignJet T650',     familyId: 't600', manualId: 't600', displayName: 'HP DesignJet T650' },
  { model: 'HP DesignJet T850',     familyId: 't850', manualId: 't850', displayName: 'HP DesignJet T850' },
  { model: 'HP DesignJet T850 MFP', familyId: 't850', manualId: 't850', displayName: 'HP DesignJet T850 MFP' },
  { model: 'HP DesignJet T950',     familyId: 't850', manualId: 't850', displayName: 'HP DesignJet T950' },
  { model: 'HP DesignJet T950 MFP', familyId: 't850', manualId: 't850', displayName: 'HP DesignJet T950 MFP' },
  { model: 'HP DesignJet T870',     familyId: 't870', manualId: 't870', displayName: 'HP DesignJet T870' },
  { model: 'HP DesignJet T870 MFP', familyId: 't870', manualId: 't870', displayName: 'HP DesignJet T870 MFP' },
  { model: 'HP DesignJet Z6',       familyId: 'z6',   manualId: 'z6',   displayName: 'HP DesignJet Z6' },
  { model: 'HP DesignJet Z6dr',     familyId: 'z6',   manualId: 'z6',   displayName: 'HP DesignJet Z6dr' },
  { model: 'HP DesignJet Z9+',      familyId: 'z6',   manualId: 'z6',   displayName: 'HP DesignJet Z9+' },
  { model: 'HP DesignJet Z9+dr',    familyId: 'z6',   manualId: 'z6',   displayName: 'HP DesignJet Z9+dr' }
];

// Parse a model string (from discovery or manual pick) and return the
// printerFamilies entry it belongs to. Returns null if unrecognised.
// Uses the same regex shape PrintPreview uses for series extraction.
export function getPrinterFamilyFromModel(modelString) {
  if (!modelString || typeof modelString !== 'string') return null;
  const match = modelString.match(/\b(X?T\d{2,3}(?:dr)?|Z\d{1,2}\+?(?:dr)?)\b/i);
  if (!match) return null;
  const token = match[1].toUpperCase().replace(/^X(?=T)/, ''); // Brightivity: XT870 → the T870 family

  // Map the detected model token to a family id.
  if (/^T2\d{2}$/.test(token)) return printerFamilies.find(f => f.id === 't200') || null;
  if (/^T6\d{2}$/.test(token)) return printerFamilies.find(f => f.id === 't600') || null;
  if (/^T8[59]0$/.test(token)) return printerFamilies.find(f => f.id === 't850') || null;
  if (/^T870$/.test(token))    return printerFamilies.find(f => f.id === 't870') || null;
  if (/^Z[69]\+?(DR)?$/.test(token)) return printerFamilies.find(f => f.id === 'z6') || null;
  return null;
}

// Given a family id, return the manual id for that family.
export function getManualIdForFamily(familyId) {
  switch (familyId) {
    case 't200':
    case 't600': return 't600';
    case 't850': return 't850';
    case 't870': return 't870';
    case 'z6':   return 'z6';
    default:     return null;
  }
}

export const fuseOptions = {
  keys: [
    { name: 'title', weight: 0.3 },
    { name: 'keywords', weight: 0.3 },
    { name: 'naturalQueries', weight: 0.25 },
    { name: 'description', weight: 0.1 },
    { name: 'category', weight: 0.05 }
  ],
  threshold: 0.4,  // Lower = stricter matching
  includeScore: true,
  ignoreLocation: true,
  useExtendedSearch: true
};

export default resources;
